package com.havn.app.features.clients.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.core.billing.HavnPlanLimits
import com.havn.app.core.billing.HavnPlans
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.localization.HavnStrings
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientDraft
import com.havn.app.features.clients.domain.ClientRepository
import com.havn.app.features.leads.domain.LeadRepository
import com.havn.app.features.openhouses.domain.LeadTemperature
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ClientsViewModel(
    private val clientRepository: ClientRepository,
    private val authRepository: AuthRepository,
    private val agencyRepository: AgencyRepository,
    private val leadRepository: LeadRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(ClientsUiState())
    val uiState: StateFlow<ClientsUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var selectedAgencyId: String? = null
    private var hasLoadedAgencyContext = false
    private var planLimits: HavnPlanLimits = HavnPlans.freeLimits

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                agentId = nextAgentId
                if (nextAgentId == null) {
                    hasLoadedAgencyContext = false
                    _uiState.value = ClientsUiState()
                } else {
                    if (!hasLoadedAgencyContext) {
                        hasLoadedAgencyContext = true
                        selectedAgencyId = null
                    }
                    loadPlanLimits(nextAgentId)
                    loadClients(nextAgentId)
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun refreshEntitlement(tier: HavnSubscriptionTier) {
        val currentAgentId = agentId ?: return
        planLimits = HavnPlans.limitsFor(tier)
        scope.launch {
            loadClients(currentAgentId)
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun clearMessage() {
        _uiState.value = _uiState.value.copy(message = null)
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun loadMoreClients() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreClients) return
        scope.launch { loadClients(currentAgentId, append = true) }
    }

    fun openShareRecipientPicker() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.shareRecipientQuery.isNotBlank()) {
            _uiState.value = state.copy(shareRecipientQuery = "", shareRecipientError = null)
            scope.launch { loadShareRecipientClients(currentAgentId) }
            return
        }
        if (state.shareRecipientClients.isNotEmpty() || state.isLoadingShareRecipients) return
        scope.launch { loadShareRecipientClients(currentAgentId) }
    }

    fun updateShareRecipientQuery(query: String) {
        val currentAgentId = agentId ?: return
        if (_uiState.value.shareRecipientQuery == query) return
        _uiState.value = _uiState.value.copy(shareRecipientQuery = query, shareRecipientError = null)
        scope.launch { loadShareRecipientClients(currentAgentId) }
    }

    fun loadMoreShareRecipients() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoadingShareRecipients || state.isLoadingMoreShareRecipients || !state.hasMoreShareRecipients) return
        scope.launch { loadShareRecipientClients(currentAgentId, append = true) }
    }

    fun setAgencyContext(agencyId: String?) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId) return
        selectedAgencyId = agencyId
        hasLoadedAgencyContext = true
        _uiState.value = _uiState.value.copy(
            shareRecipientClients = emptyList(),
            hasMoreShareRecipients = false,
            shareRecipientError = null
        )
        val currentAgentId = agentId ?: return
        scope.launch { loadClients(currentAgentId) }
    }

    fun startAdd() {
        val state = _uiState.value
        if (state.clients.size >= planLimits.maxContacts) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxContacts} contacts. Upgrade to add more.")
            return
        }
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Add,
            selectedClientId = null,
            form = ClientFormState(),
            message = null,
            error = null
        )
    }

    fun openClient(clientId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Detail,
            selectedClientId = clientId,
            isDetailLoading = true,
            error = null
        )
        scope.launch {
            clientRepository.clientDetail(currentAgentId, clientId)
                .onSuccess {
                    replaceClient(it)
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun openList() {
        _uiState.value = _uiState.value.copy(mode = ClientsMode.List, selectedClientId = null, form = ClientFormState(), showDeleteConfirmation = false)
    }

    fun startGlobalInquiry() {
        if (_uiState.value.selectedClientId == null) return
        _uiState.value = _uiState.value.copy(mode = ClientsMode.AddInquiry, inquiryForm = GlobalInquiryForm(), error = null)
    }

    fun openGeneralInquiry(leadId: String) {
        val inquiry = _uiState.value.selectedClient?.inquiries?.firstOrNull { it.leadId == leadId && it.propertyId == null } ?: return
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.EditInquiry,
            selectedInquiryLeadId = leadId,
            inquiryForm = GlobalInquiryForm(
                location = inquiry.desiredLocation.orEmpty(), radiusKm = inquiry.locationRadiusKm?.toString().orEmpty(),
                budgetMin = inquiry.budgetMin?.toString().orEmpty(), budgetMax = inquiry.budgetMax?.toString().orEmpty(),
                timeline = inquiry.purchaseTimeline.orEmpty(), propertyType = inquiry.desiredPropertyType.orEmpty(), notes = inquiry.notes.orEmpty(), source = inquiry.source.orEmpty(),
                temperature = LeadTemperature.fromDatabase(inquiry.temperature)
            ), error = null
        )
    }

    fun cancelGlobalInquiry() { _uiState.value = _uiState.value.copy(mode = ClientsMode.Detail, inquiryForm = GlobalInquiryForm(), error = null) }
    fun updateInquiryForm(form: GlobalInquiryForm) { _uiState.value = _uiState.value.copy(inquiryForm = form, error = null) }
    fun saveGlobalInquiry() {
        val currentAgentId = agentId ?: return
        val contactId = _uiState.value.selectedClientId ?: return
        val form = _uiState.value.inquiryForm
        if (form.location.isBlank()) { _uiState.value = _uiState.value.copy(error = "Location is required."); return }
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            leadRepository.createBuyerInquiry(currentAgentId, contactId, form.location, form.radiusKm.toIntOrNull(),
                form.budgetMin.toLongOrNull(), form.budgetMax.toLongOrNull(), form.timeline, form.propertyType, form.notes, form.source, form.temperature)
                .onSuccess {
                    clientRepository.clientDetail(currentAgentId, contactId).onSuccess(::replaceClient)
                    _uiState.value = _uiState.value.copy(isSaving = false, mode = ClientsMode.Detail, inquiryForm = GlobalInquiryForm(), message = "General inquiry added to leads.")
                }.onFailure { _uiState.value = _uiState.value.copy(isSaving = false); setError(it) }
        }
    }

    fun saveGeneralInquiryChanges() {
        val currentAgentId = agentId ?: return
        val contactId = _uiState.value.selectedClientId ?: return
        val leadId = _uiState.value.selectedInquiryLeadId ?: return
        val form = _uiState.value.inquiryForm
        if (form.location.isBlank()) { _uiState.value = _uiState.value.copy(error = "Location is required."); return }
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            leadRepository.updateBuyerInquiry(currentAgentId, leadId, form.location, form.radiusKm.toIntOrNull(), form.budgetMin.toLongOrNull(), form.budgetMax.toLongOrNull(), form.timeline, form.propertyType, form.notes, form.source, form.temperature)
                .onSuccess {
                    clientRepository.clientDetail(currentAgentId, contactId).onSuccess(::replaceClient)
                    _uiState.value = _uiState.value.copy(isSaving = false, mode = ClientsMode.Detail, selectedInquiryLeadId = null, inquiryForm = GlobalInquiryForm(), message = "General inquiry updated.")
                }.onFailure { _uiState.value = _uiState.value.copy(isSaving = false); setError(it) }
        }
    }

    fun updateForm(transform: ClientFormState.() -> ClientFormState) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun updatePendingPhoto(photo: HavnPickedPhoto?) {
        if (photo == null || photo.bytes.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Choose a valid contact photo.")
            return
        }
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(pendingPhoto = photo),
            error = null,
            message = null
        )
    }

    fun removePendingPhoto() {
        _uiState.value = _uiState.value.copy(
            form = _uiState.value.form.copy(pendingPhoto = null),
            error = null,
            message = null
        )
    }

    fun saveClient(strings: HavnStrings) {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        val validation = state.form.validate(strings)
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }
        if (state.clients.size >= planLimits.maxContacts) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxContacts} contacts. Upgrade to add more.")
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null, message = null)
        scope.launch {
            clientRepository.addClient(currentAgentId, state.form.toDraft(), selectedAgencyId)
                .onSuccess { client ->
                    val savedClient = state.form.pendingPhoto?.let { photo ->
                        clientRepository.updateClientPhoto(currentAgentId, client.id, photo)
                            .onFailure {
                                _uiState.value = _uiState.value.copy(isSaving = false)
                                setError(it)
                                return@launch
                            }
                            .getOrElse { client }
                    } ?: client
                    replaceClient(savedClient)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        mode = ClientsMode.Detail,
                        selectedClientId = savedClient.id,
                        form = ClientFormState(),
                        message = "Contact created"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEdit() {
        val client = _uiState.value.selectedClient ?: return
        _uiState.value = _uiState.value.copy(
            mode = ClientsMode.Edit,
            form = client.toFormState(),
            message = null,
            error = null
        )
    }

    fun cancelForm() {
        val selectedId = _uiState.value.selectedClientId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) ClientsMode.List else ClientsMode.Detail,
            form = ClientFormState(),
            error = null
        )
    }

    fun saveEditedClient(strings: HavnStrings) {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        val state = _uiState.value
        val validation = state.form.validate(strings)
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null, message = null)
        scope.launch {
            clientRepository.updateClient(currentAgentId, clientId, state.form.toDraft())
                .onSuccess { client ->
                    val savedClient = state.form.pendingPhoto?.let { photo ->
                        clientRepository.updateClientPhoto(currentAgentId, client.id, photo)
                            .onFailure {
                                _uiState.value = _uiState.value.copy(isSaving = false)
                                setError(it)
                                return@launch
                            }
                            .getOrElse { client }
                    } ?: client
                    replaceClient(savedClient)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        mode = ClientsMode.Detail,
                        selectedClientId = savedClient.id,
                        form = ClientFormState(),
                        message = "Contact saved"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun requestDeleteSelected() {
        if (_uiState.value.selectedClientId == null) return
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = true, error = null)
    }

    fun dismissDeleteConfirmation() {
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = false)
    }

    fun confirmDeleteSelected() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, showDeleteConfirmation = false, error = null, message = null)
        scope.launch {
            clientRepository.deleteClient(currentAgentId, clientId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        clients = _uiState.value.clients.filterNot { it.id == clientId },
                        selectedClientId = null,
                        mode = ClientsMode.List,
                        form = ClientFormState(),
                        isSaving = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun sendQrCode(clientId: String, strings: HavnStrings) {
        val currentAgentId = agentId ?: return
        val client = _uiState.value.clients.firstOrNull { it.id == clientId } ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        if (!havnIsWebShell) {
            val opened = HavnExternalActions.shareQrImage(
                title = strings.sendQrShareTitle,
                message = strings.sendQrShareBody.trim(),
                fileName = "${client.fullName.ifBlank { "havn-contact" }}-qr.png",
                payload = client.qrPayload,
                email = client.email,
                phone = client.phone
            )
            _uiState.value = _uiState.value.copy(
                isSaving = false,
                message = if (opened) strings.sendQrReadyMessage else null,
                error = if (opened) null else strings.sendQrUnavailableError
            )
            return
        }
        scope.launch {
            clientRepository.sendQrCode(currentAgentId, clientId)
                .onSuccess { assets ->
                    val body = buildString {
                        append(strings.sendQrShareBody.trim())
                        append("\n\nQR code:\n")
                        append(assets.qrCodeUrl)
                        assets.walletPassUrl?.takeIf { it.isNotBlank() }?.let { passUrl ->
                            append("\n\nWallet pass (.pkpass):\n")
                            append(passUrl)
                        }
                    }
                    val opened = client.email?.takeIf { it.isNotBlank() }?.let { email ->
                        HavnExternalActions.openEmail(
                            to = email,
                            subject = strings.sendQrShareTitle,
                            body = body
                        )
                    } ?: client.phone?.takeIf { it.isNotBlank() }?.let { phone ->
                        HavnExternalActions.openSms(
                            to = phone,
                            body = body
                        )
                    } ?: false
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        message = if (opened) strings.sendQrReadyMessage else null,
                        error = if (opened) null else strings.sendQrUnavailableError
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEditingComments() {
        val client = _uiState.value.selectedClient ?: return
        _uiState.value = _uiState.value.copy(commentsDraft = client.notes.orEmpty(), isEditingComments = true)
    }

    fun updateCommentsDraft(value: String) {
        _uiState.value = _uiState.value.copy(commentsDraft = value, error = null)
    }

    fun saveComments() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        val comments = _uiState.value.commentsDraft
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.updateClientNotes(currentAgentId, clientId, comments)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        isEditingComments = false,
                        commentsDraft = "",
                        message = "Comments saved"
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun uploadClientPhoto(photo: HavnPickedPhoto?) {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        if (photo == null || photo.bytes.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Choose a valid contact photo.")
            return
        }
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.updateClientPhoto(currentAgentId, clientId, photo)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(isSaving = false, message = "Photo updated")
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun deleteClientPhoto() {
        val currentAgentId = agentId ?: return
        val clientId = _uiState.value.selectedClientId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, message = null, error = null)
        scope.launch {
            clientRepository.deleteClientPhoto(currentAgentId, clientId)
                .onSuccess { client ->
                    replaceClient(client)
                    _uiState.value = _uiState.value.copy(isSaving = false, message = "Photo deleted")
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun loadClients(agentId: String, append: Boolean = false) {
        val state = _uiState.value
        val visibleLimit = planLimits.maxContacts
        if (append && state.clients.size >= visibleLimit) {
            _uiState.value = state.copy(hasMoreClients = false)
            return
        }
        val offset = if (append) state.clients.size else 0
        val pageLimit = if (visibleLimit == Int.MAX_VALUE) CLIENT_PAGE_SIZE else (visibleLimit - offset).coerceAtMost(CLIENT_PAGE_SIZE)
        if (pageLimit <= 0) {
            _uiState.value = state.copy(isLoading = false, isLoadingMore = false, hasMoreClients = false)
            return
        }
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        clientRepository.clientPage(
            agentId = agentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = pageLimit
        )
            .onSuccess { clients ->
                val merged = if (append) (_uiState.value.clients + clients).distinctBy { it.id } else clients
                val visibleClients = if (visibleLimit == Int.MAX_VALUE) merged else merged.take(visibleLimit)
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    hasLoadedInitialPage = _uiState.value.hasLoadedInitialPage || !append,
                    clients = visibleClients,
                    hasMoreClients = clients.size == pageLimit && visibleClients.size < visibleLimit
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    hasLoadedInitialPage = _uiState.value.hasLoadedInitialPage || !append
                )
                setError(it)
        }
    }

    private suspend fun loadPlanLimits(currentAgentId: String) {
        agencyRepository.entitlementForUser(currentAgentId)
            .onSuccess { entitlement ->
                planLimits = HavnPlans.limitsFor(entitlement.tier)
            }
            .onFailure {
                HavnLogger.error("ClientsViewModel", "Failed to load plan limits", it)
                planLimits = HavnPlans.freeLimits
            }
    }

    private suspend fun loadShareRecipientClients(agentId: String, append: Boolean = false) {
        val state = _uiState.value
        val offset = if (append) state.shareRecipientClients.size else 0
        _uiState.value = if (append) {
            state.copy(isLoadingMoreShareRecipients = true, shareRecipientError = null)
        } else {
            state.copy(isLoadingShareRecipients = true, shareRecipientError = null)
        }
        clientRepository.clientPage(
            agentId = agentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.shareRecipientQuery,
            offset = offset,
            limit = CLIENT_PAGE_SIZE
        )
            .onSuccess { clients ->
                _uiState.value = _uiState.value.copy(
                    isLoadingShareRecipients = false,
                    isLoadingMoreShareRecipients = false,
                    shareRecipientClients = if (append) {
                        (_uiState.value.shareRecipientClients + clients).distinctBy { it.id }
                    } else {
                        clients
                    },
                    hasMoreShareRecipients = clients.size == CLIENT_PAGE_SIZE
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(
                    isLoadingShareRecipients = false,
                    isLoadingMoreShareRecipients = false,
                    shareRecipientError = it.havnFriendlyMessage("Could not load contacts. Please try again.")
                )
                HavnLogger.error("ClientsViewModel", "Share recipient contacts failed", it)
            }
    }

    private fun replaceClient(client: Client) {
        val current = _uiState.value.clients
        val existingIndex = current.indexOfFirst { it.id == client.id }
        val updated = if (existingIndex >= 0) {
            current.toMutableList().apply { this[existingIndex] = client }
        } else {
            listOf(client) + current
        }
        _uiState.value = _uiState.value.copy(
            clients = updated
        )
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("ClientsViewModel", "User-visible clients error", error)
        _uiState.value = _uiState.value.copy(
            error = error.havnFriendlyMessage("Could not load contacts. Please try again.")
        )
    }
}

data class ClientsUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val hasLoadedInitialPage: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val message: String? = null,
    val clients: List<Client> = emptyList(),
    val searchQuery: String = "",
    val hasMoreClients: Boolean = false,
    val shareRecipientClients: List<Client> = emptyList(),
    val shareRecipientQuery: String = "",
    val isLoadingShareRecipients: Boolean = false,
    val isLoadingMoreShareRecipients: Boolean = false,
    val hasMoreShareRecipients: Boolean = false,
    val shareRecipientError: String? = null,
    val selectedClientId: String? = null,
    val mode: ClientsMode = ClientsMode.List,
    val form: ClientFormState = ClientFormState(),
    val commentsDraft: String = "",
    val isEditingComments: Boolean = false,
    val showDeleteConfirmation: Boolean = false
    ,val inquiryForm: GlobalInquiryForm = GlobalInquiryForm()
    ,val selectedInquiryLeadId: String? = null
) {
    val selectedClient: Client?
        get() = clients.firstOrNull { it.id == selectedClientId }
}

data class ClientFormState(
    val firstName: String = "",
    val lastName: String = "",
    val email: String = "",
    val phone: String = "",
    val notes: String = "",
    val pendingPhoto: HavnPickedPhoto? = null,
    val validationError: String? = null
) {
    fun validate(strings: HavnStrings): String? = when {
        firstName.trim().isBlank() -> "First name is required."
        lastName.trim().isBlank() -> "Last name is required."
        email.trim().isBlank() && phone.trim().isBlank() -> strings.clientQrDeliveryRequired
        email.isNotBlank() && !email.contains("@") -> "Enter a valid email address."
        else -> null
    }

    fun toDraft(): ClientDraft = ClientDraft(
        firstName = firstName.trim(),
        lastName = lastName.trim(),
        email = email.trim().takeIf { it.isNotBlank() },
        phone = phone.trim().takeIf { it.isNotBlank() },
        notes = notes.trim().takeIf { it.isNotBlank() }
    )
}

enum class ClientsMode {
    List,
    Add,
    Edit,
    Detail,
    AddInquiry,
    EditInquiry
}

data class GlobalInquiryForm(val location: String = "", val radiusKm: String = "", val budgetMin: String = "", val budgetMax: String = "", val timeline: String = "", val propertyType: String = "", val notes: String = "", val source: String = "", val temperature: LeadTemperature = LeadTemperature.Warm)

private fun Client.toFormState(): ClientFormState = ClientFormState(
    firstName = firstName.orEmpty().ifBlank { fullName.substringBefore(" ") },
    lastName = lastName.orEmpty().ifBlank { fullName.substringAfter(" ", "") },
    email = email.orEmpty(),
    phone = phone.orEmpty(),
    notes = notes.orEmpty()
)

private const val CLIENT_PAGE_SIZE = 20
